/**
 * Restore a list screen's scroll position on back-navigation, once the list
 * actually has content.
 *
 * WHY THE ROUTER'S OWN RESTORATION IS NOT ENOUGH. `scrollRestoration: true`
 * (main.tsx) does snapshot the shell's `<main>` correctly — verified: leaving
 * `/products` scrolled to 2200px records exactly that. What fails is the replay.
 * The router re-applies the offset on its `onRendered` event, which fires the
 * moment the route component commits — and every list on this screen comes from
 * a Dexie `useLiveQuery`, which returns `[]` synchronously and resolves a tick
 * later. So the router assigns `scrollTop = 2200` to a `<main>` that is still
 * one viewport tall, the browser clamps it to 0, and by the time the products
 * arrive nothing re-applies it. The symptom is "scroll a few rows, open a
 * product, come back, and you're at the top" — at any depth, not just deep ones.
 *
 * So this hook waits for `ready` (the caller's "the list has rendered" signal)
 * and applies the saved offset itself, exactly once per mount. It is additive:
 * the router's own attempt still runs first and simply does nothing.
 *
 * The offset also has to be *reachable*, which is the other half of the fix —
 * see `useIncrementalList`'s `restoreKey`. A restored window of 48 cards is far
 * shorter than the scroll position the user left from, and this hook would clamp
 * for the same reason the router did.
 */
import { useEffect, useRef } from 'react';
import { useElementScrollRestoration } from '@tanstack/react-router';

/** The shell's scrollable `<main>` (components/shell/app-shell.tsx). */
export const APP_MAIN_SCROLL_ID = 'app-main';

export function useRestoreListScroll(ready: boolean): void {
  const entry = useElementScrollRestoration({ id: APP_MAIN_SCROLL_ID });
  const appliedRef = useRef(false);

  useEffect(() => {
    if (appliedRef.current || !ready || entry === undefined) return;
    const main = document.querySelector(`[data-scroll-restoration-id="${APP_MAIN_SCROLL_ID}"]`);
    if (!main) return;
    // Once per mount: a later scroll by the user must not be undone, and a
    // fresh forward navigation has no entry to apply in the first place.
    appliedRef.current = true;
    main.scrollTop = entry.scrollY;
  }, [ready, entry]);
}
